import { createI18n } from 'vue-i18n'
import { App, Plugin } from 'vue'
import { LocaleMessages } from '@intlify/core-base'
// Create a type definition for messages instead of importing it
type AppMessages = Record<string, Record<string, any>>;
import type { TranslateOptions } from 'vue-i18n'

// Define available locales
export type LocaleType = 'en' | 'vi'

export const LOCALES = {
  EN: 'en' as LocaleType,
  VI: 'vi' as LocaleType
}

// Locale mặc định, luôn được sử dụng khi không có locale khác
export const DEFAULT_LOCALE = LOCALES.EN;

// Get browser locale or default to English
function getBrowserLocale(): LocaleType {
  try {
    // Trong môi trường không có window (SSR hoặc test), trả về mặc định
    if (typeof window === 'undefined' || typeof navigator === 'undefined') {
      return DEFAULT_LOCALE;
    }
    
    const navigatorLocale = navigator.languages !== undefined ? 
      navigator.languages[0] : 
      navigator.language

    if (!navigatorLocale) {
      return DEFAULT_LOCALE;
    }

    const locale = navigatorLocale.trim().split(/-|_/)[0]
    if (Object.values(LOCALES).includes(locale as LocaleType)) {
      return locale as LocaleType
    }

    return DEFAULT_LOCALE;
  } catch (e) {
    console.error('Error detecting browser locale:', e);
    return DEFAULT_LOCALE;
  }
}

// Load stored locale from localStorage or use browser locale
function getStoredLocale(): LocaleType {
  try {
    // Trong môi trường không có window (SSR hoặc test), trả về mặc định
    if (typeof window === 'undefined' || typeof localStorage === 'undefined') {
      return DEFAULT_LOCALE;
    }
    
    const storedLocale = localStorage.getItem('locale')
    if (storedLocale && Object.values(LOCALES).includes(storedLocale as LocaleType)) {
      return storedLocale as LocaleType
    }
    return getBrowserLocale()
  } catch (e) {
    console.error('Error reading stored locale:', e);
    return DEFAULT_LOCALE;
  }
}

// Tạo mặc định với ít nhất vài key cơ bản để tránh lỗi
const basicMessages: Record<string, any> = {
  en: {
    common: {
      loading: 'Loading...',
      error: 'Error',
      retry: 'Retry'
    }
  },
  vi: {
    common: {
      loading: 'Đang tải...',
      error: 'Lỗi',
      retry: 'Thử lại' 
    }
  }
};

// Fix the import path to correctly point to the TypeScript locale files
const localeModules = import.meta.glob('./locales/*.ts', { eager: true }) as Record<string, { default: Record<string, any> }>;

// Convert the messages to the expected format
const formattedMessages: Record<string, Record<string, any>> = {};
Object.entries(localeModules).forEach(([path, module]) => {
  const locale = path.match(/\/([^/]+)\.ts$/)?.[1];
  if (locale) {
    formattedMessages[locale] = { ...basicMessages[locale as LocaleType], ...module.default };
  }
});

// Create i18n instance with the correctly formatted messages
const i18n = createI18n({
  legacy: true, // Chuyển lại về legacy mode để tương thích với ứng dụng hiện tại
  locale: getStoredLocale(), 
  fallbackLocale: DEFAULT_LOCALE,
  messages: formattedMessages,
  silentFallbackWarn: true,
  missingWarn: false,
  fallbackWarn: false,
  flatJson: false,
  warnHtmlMessage: false,
  escapeParameter: true
})

// Debug mode flag - set to false by default
const DEBUG_MODE = false

/**
 * Hàm preload các locale messages để chuẩn bị cho việc chuyển đổi ngôn ngữ
 */
function preloadLocaleMessages(currentLocale: LocaleType): void {
  // Preload English if current locale is not English
  if (currentLocale !== LOCALES.EN) {
    setTimeout(() => {
      loadLocaleMessages(LOCALES.EN)
        .then(() => {
          console.log(`[i18n] Preloaded locale: ${LOCALES.EN}`);
        })
        .catch(err => {
          console.error(`[i18n] Failed to preload locale: ${LOCALES.EN}`, err);
        });
    }, 1000);
  }

  // Preload Vietnamese if current locale is not Vietnamese
  if (currentLocale !== LOCALES.VI) {
    setTimeout(() => {
      loadLocaleMessages(LOCALES.VI)
        .then(() => {
          console.log(`[i18n] Preloaded locale: ${LOCALES.VI}`);
        })
        .catch(err => {
          console.error(`[i18n] Failed to preload locale: ${LOCALES.VI}`, err);
        });
    }, 1000);
  }
}

// Preload ngôn ngữ khác sau một khoảng thời gian ngắn
// để tránh block việc tải trang
setTimeout(() => {
  preloadLocaleMessages(i18n.global.locale.value as LocaleType);
}, 1000);

// Load locale messages
async function loadLocaleMessages(locale: LocaleType) {
  try {
    // Tránh nạp lại nếu đã có đủ messages (hơn 50 key)
    const existingMessageCount = Object.keys(i18n.global.messages.value[locale] || {}).length;
    if (existingMessageCount > 50) {
      console.log(`Locale ${locale} already loaded with ${existingMessageCount} entries, skipping load`);
      return;
    }
    
    console.log(`Loading locale: ${locale}`);
    
    // Production: Load from public/locales
    if (import.meta.env.PROD) {
      const url = `${import.meta.env.BASE_URL}locales/${locale}.js`;
      
      console.log(`[i18n DEBUG] Trying to load locale from: ${url}`);
      console.log(`[i18n DEBUG] BASE_URL is: ${import.meta.env.BASE_URL}`);
      console.log(`[i18n DEBUG] Full URL is: ${window.location.origin}${import.meta.env.BASE_URL}locales/${locale}.js`);
      
      // Debug: Thử fetch trực tiếp file JS để kiểm tra nội dung
      try {
        fetch(`${window.location.origin}${import.meta.env.BASE_URL}locales/${locale}.js`)
          .then(response => {
            console.log(`[i18n DEBUG] Fetch status: ${response.status}`);
            return response.text();
          })
          .then(text => {
            console.log(`[i18n DEBUG] First 100 chars of fetched JS: ${text.substring(0, 100)}...`);
            console.log(`[i18n DEBUG] JS file length: ${text.length} chars`);
          })
          .catch(err => {
            console.error(`[i18n DEBUG] Fetch failed: ${err.message}`);
          });
      } catch (e) {
        console.error(`[i18n DEBUG] Failed to fetch locale file: ${e}`);
      }
      
      try {
        // Check if script already exists to avoid duplicates
        const existingScript = document.querySelector(`script[src="${url}"]`);
        if (existingScript) {
          console.log(`[i18n DEBUG] Script for ${locale} already exists, checking data`);
          // Thêm thời gian chờ để đảm bảo script đã load
          await new Promise(resolve => setTimeout(resolve, 100));
          
          const localeData = (window as any)[locale as string];
          console.log(`[i18n DEBUG] Window locale data exists: ${Boolean(localeData)}`);
          console.log(`[i18n DEBUG] Window locale data keys: ${localeData ? Object.keys(localeData).length : 'none'}`);
          if (localeData && Object.keys(localeData).length > 0) {
            // Merge với basic messages để đảm bảo always có những key cơ bản
            console.log(`[i18n DEBUG] Setting locale messages for ${locale} from existing script`);
            console.log(`[i18n DEBUG] Structure before merge:`, Object.keys(localeData).map(k => `${k}: ${typeof localeData[k]}`));
            
            // Tạo deep clone để đảm bảo tất cả thuộc tính được sao chép đúng
            const deepClone = JSON.parse(JSON.stringify(localeData));
            
            // Gán trực tiếp localeData thay vì merge với basicMessages
            i18n.global.setLocaleMessage(locale, deepClone);
            
            // Debug reports structure
            debugReportsStructure(locale);
            
            // Bổ sung log để kiểm tra
            setTimeout(() => {
              console.log(`[i18n DEBUG] Raw locale message after set:`, i18n.global.getLocaleMessage(locale));
              console.log(`[i18n DEBUG] Test key exists:`, i18n.global.te('reports.table.category'));
              console.log(`[i18n DEBUG] Test key value:`, i18n.global.t('reports.table.category'));
            }, 500);
            
            console.log(`Locale ${locale} loaded from existing script with ${Object.keys(localeData).length} entries`);
            return;
          }
        }
        
        console.log(`[i18n DEBUG] Loading locale script from ${url}`);
        const script = document.createElement('script');
        script.src = url;
        script.async = true;
        script.id = `locale-${locale}`;
        
        // When script loaded, window will have 'vi' or 'en' variable
        await new Promise<void>((resolve, reject) => {
          let timer: any = null;
          
          // Set timeout để tránh hanging nếu script không load
          const timeout = setTimeout(() => {
            console.warn(`[i18n DEBUG] Script load timeout for ${locale}`);
            // Vẫn phải reject để chuyển sang fallback
            reject(new Error(`Script load timeout for ${locale}`));
          }, 5000);
          
          script.onload = () => {
            console.log(`[i18n DEBUG] Script for ${locale} loaded, checking for data`);
            clearTimeout(timeout);
            
            // Thêm thời gian chờ để đảm bảo script đã chạy
            timer = setTimeout(() => {
              const localeData = (window as any)[locale as string];
              console.log(`[i18n DEBUG] After script load - Window locale data exists: ${Boolean(localeData)}`);
              console.log(`[i18n DEBUG] After script load - Window locale data keys: ${localeData ? Object.keys(localeData).length : 'none'}`);
              
              // Log ra 11 entries để kiểm tra
              if (localeData) {
                console.log(`[i18n DEBUG] Locale data entries:`, localeData);
                Object.keys(localeData).forEach(key => {
                  console.log(`[i18n DEBUG] Top-level key: ${key}`);
                });
              }
              
              if (localeData && Object.keys(localeData).length > 0) {
                // Merge với basic messages để đảm bảo always có những key cơ bản
                console.log(`[i18n DEBUG] Setting locale messages for ${locale} from loaded script`);
                console.log(`[i18n DEBUG] Structure before merge:`, Object.keys(localeData).map(k => `${k}: ${typeof localeData[k]}`));
                
                // Tạo deep clone để đảm bảo tất cả thuộc tính được sao chép đúng
                const deepClone = JSON.parse(JSON.stringify(localeData));
                
                // Gán trực tiếp localeData thay vì merge
                i18n.global.setLocaleMessage(locale, deepClone);
                
                // Debug reports structure
                debugReportsStructure(locale);
                
                // Bổ sung log để kiểm tra
                setTimeout(() => {
                  console.log(`[i18n DEBUG] Raw locale message after set:`, i18n.global.getLocaleMessage(locale));
                  console.log(`[i18n DEBUG] Test key exists:`, i18n.global.te('reports.table.category'));
                  console.log(`[i18n DEBUG] Test key value:`, i18n.global.t('reports.table.category'));
                }, 500);
                
                console.log(`Locale ${locale} loaded from ${url} with ${Object.keys(localeData).length} entries`);
                resolve();
              } else {
                const error = `Failed to load locale data for ${locale} from ${url} - data not found in window.${locale}`;
                console.error(error);
                reject(new Error(error));
              }
            }, 200);
          };
          
          script.onerror = (e) => {
            clearTimeout(timeout);
            clearTimeout(timer);
            const error = `[i18n DEBUG] Failed to load script for locale ${locale} from ${url}: ${e}`;
            console.error(error);
            reject(new Error(error));
          };
          
          document.head.appendChild(script);
        }).catch(err => {
          console.error(`Error loading script, falling back to direct import: ${err.message}`);
          
          // Fallback to direct import
          return import(`./locales/${locale}.ts`).then(module => {
            if (module.default) {
              // Merge với basic messages để đảm bảo always có những key cơ bản
              i18n.global.setLocaleMessage(locale, {
                ...basicMessages[locale],
                ...module.default
              });
              console.log(`Locale ${locale} loaded from direct import fallback with ${Object.keys(module.default).length} entries`);
            } else {
              throw new Error(`Fallback module loaded for ${locale} but contains no data`);
            }
          });
        });
      } catch (err) {
        console.error(`All loading methods failed for ${locale}, using basic fallback`);
        // Final fallback to ensure we at least have basic messages
        if (!i18n.global.messages.value[locale] || Object.keys(i18n.global.messages.value[locale]).length === 0) {
          i18n.global.setLocaleMessage(locale, basicMessages[locale]);
        }
        
        // If current locale failed but not English, try to ensure English is loaded as fallback
        if (locale !== DEFAULT_LOCALE && 
            (!i18n.global.messages.value[DEFAULT_LOCALE] || 
             Object.keys(i18n.global.messages.value[DEFAULT_LOCALE]).length <= 3)) {
          await loadLocaleMessages(DEFAULT_LOCALE).catch(() => {
            // If even English load fails, at least ensure basic English messages
            i18n.global.setLocaleMessage(DEFAULT_LOCALE, basicMessages[DEFAULT_LOCALE]);
          });
        }
      }
    } 
    // Development: Load from modules
    else {
      try {
        const module = await import(`./locales/${locale}.ts`);
        if (module.default && Object.keys(module.default).length > 0) {
          // Merge với basic messages để đảm bảo always có những key cơ bản
          i18n.global.setLocaleMessage(locale, {
            ...basicMessages[locale],
            ...module.default
          });
          console.log(`Locale ${locale} loaded from module with ${Object.keys(module.default).length} entries`);
        } else {
          throw new Error(`Module loaded for ${locale} but contains no data`);
        }
      } catch (error) {
        console.error(`Failed to load locale module for ${locale}:`, error);
        
        // Ensure we at least have basic messages
        if (!i18n.global.messages.value[locale] || Object.keys(i18n.global.messages.value[locale]).length === 0) {
          i18n.global.setLocaleMessage(locale, basicMessages[locale]);
        }
        
        throw error;
      }
    }
    
    // Ensure reactivity by recomputing message keys
    console.log(`Locale ${locale} has ${Object.keys(i18n.global.messages.value[locale]).length} entries`);
    
    // Debug deeper vào i18n message
    console.log(`[i18n DEBUG] Post-processing check - Top level keys in i18n:`, Object.keys(i18n.global.messages.value[locale]));
    
    // Duyệt qua các top-level key xem có bao nhiêu sub-keys
    Object.keys(i18n.global.messages.value[locale]).forEach(topKey => {
      const val = i18n.global.messages.value[locale][topKey];
      if (typeof val === 'object') {
        console.log(`[i18n DEBUG] Top key "${topKey}" has ${Object.keys(val).length} sub-keys`);
      }
    });
  } catch (error) {
    console.error(`Failed to load locale ${locale}:`, error);
    throw error;
  }
}

// Thêm hàm debug cụ thể cho reports
function debugReportsStructure(locale: LocaleType) {
  setTimeout(() => {
    try {
      const messages = i18n.global.getLocaleMessage(locale) as any;
      
      console.log(`[i18n DEBUG] ---- Debugging reports structure ----`);
      if (messages && messages.reports) {
        console.log(`[i18n DEBUG] reports exists as a top-level key`);
        console.log(`[i18n DEBUG] reports keys:`, Object.keys(messages.reports));
        
        if (messages.reports.table) {
          console.log(`[i18n DEBUG] reports.table exists with keys:`, Object.keys(messages.reports.table));
          console.log(`[i18n DEBUG] reports.table full object:`, messages.reports.table);
          
          if (messages.reports.table.category) {
            console.log(`[i18n DEBUG] reports.table.category value:`, messages.reports.table.category);
          } else {
            console.log(`[i18n DEBUG] reports.table.category doesn't exist`);
          }
        } else {
          console.log(`[i18n DEBUG] reports.table doesn't exist`);
        }
      } else {
        console.log(`[i18n DEBUG] reports doesn't exist as a top-level key`);
      }
      
      // Try direct access with string path
      const test1 = i18n.global.t('reports.table.category');
      console.log(`[i18n DEBUG] Direct t('reports.table.category'): ${test1}`);
      
      // Try with array path
      const test2 = i18n.global.t(['reports','table','category'].join('.'));
      console.log(`[i18n DEBUG] Array path t(['reports','table','category']): ${test2}`);
      
      // Try with direct resolvedMessage access to bypass translation mechanism
      try {
        const messages = i18n.global.getLocaleMessage(locale);
        // Use type assertion with Record to avoid excessive type depth
        const typedMessages = messages as Record<string, any>;
        if (typedMessages.reports?.table?.optional?.category) {
          console.log(`[i18n DEBUG] Direct access value:`, typedMessages.reports.table.category);
        }
      } catch (e) {
        console.error(`[i18n DEBUG] Error during direct access:`, e);
      }
    } catch (e) {
      console.error(`[i18n DEBUG] Error during reports structure debug:`, e);
    }
  }, 1000);
}

/**
 * Helper function that safely gets a nested message from the translations
 * without causing TypeScript recursion issues
 */
export function getNestedMessage(path: string, locale?: LocaleType, fallback?: string): string {
  try {
    // Get current locale if not specified
    const currentLocale = locale || i18n.global.locale.value as LocaleType;
    
    // Cast to a simpler type to avoid TypeScript recursion issues
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const rawMessages: any = i18n.global.getLocaleMessage(currentLocale);
    
    // Split the path into parts
    const parts = path.split('.');
    
    // Navigate through the messages object
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    let current: any = rawMessages;
    
    // Simple iteration without complex type checking
    for (const part of parts) {
      // Check if current is an object and has the property
      if (current === null || current === undefined || typeof current !== 'object') {
        // Path navigation failed - try fallback locale if different from current
        if (!locale && currentLocale !== DEFAULT_LOCALE) {
          return getNestedMessage(path, DEFAULT_LOCALE, fallback);
        }
        return fallback !== undefined ? fallback : path;
      }
      
      // Move to next level
      current = current[part];
      
      // If undefined or null, path doesn't exist
      if (current === undefined || current === null) {
        // Try fallback locale if different from current
        if (!locale && currentLocale !== DEFAULT_LOCALE) {
          return getNestedMessage(path, DEFAULT_LOCALE, fallback);
        }
        return fallback !== undefined ? fallback : path;
      }
    }
    
    // If we've reached a string, return it
    if (typeof current === 'string') {
      return current;
    }
    
    // If it's not a string (object/array/etc), use fallback
    return fallback !== undefined ? fallback : path;
  } catch (error) {
    console.error(`[i18n] Error accessing nested message for path "${path}":`, error);
    return fallback !== undefined ? fallback : path;
  }
}

// Type guard function to safely check if an object is a record
function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

// Helper function to change locale
export function setLocale(locale: LocaleType) {
  if (!Object.values(LOCALES).includes(locale)) {
    console.error(`Invalid locale: ${locale}`);
    return;
  }
  
  // Không làm gì nếu locale hiện tại đã là locale cần set
  if (i18n.global.locale.value === locale) {
    console.log(`[i18n DEBUG] Skipping locale change - already using ${locale}`);
    return;
  }
  
  console.log(`[i18n DEBUG] Setting locale to ${locale}`);
  console.log(`[i18n DEBUG] Current vue-i18n locale: ${i18n.global.locale.value}`);
  console.log(`[i18n DEBUG] Current messages count: ${i18n.global.messages.value[locale] ? Object.keys(i18n.global.messages.value[locale]).length : 0}`);
  
  try {
    // Save to localStorage for persistence 
    if (typeof localStorage !== 'undefined') {
      localStorage.setItem('locale', locale);
      console.log(`[i18n DEBUG] Saved locale to localStorage: ${locale}`);
    }
    
    // Update HTML lang attribute
    document.querySelector('html')?.setAttribute('lang', locale);
    console.log(`[i18n DEBUG] Updated HTML lang attribute to: ${locale}`);
  } catch (e) {
    console.error('[i18n DEBUG] Error setting locale metadata:', e);
  }
  
  // IMPORTANT: Load messages before switching locale to ensure they're available
  const hasMessages = i18n.global.messages.value[locale] && 
                      Object.keys(i18n.global.messages.value[locale]).length > 3;
  
  console.log(`[i18n DEBUG] Locale has sufficient messages: ${hasMessages}`);
  console.log(`[i18n DEBUG] Messages count: ${i18n.global.messages.value[locale] ? Object.keys(i18n.global.messages.value[locale]).length : 0}`);
  
  if (!hasMessages) {
    // Load messages first before changing locale
    loadLocaleMessages(locale)
      .then(() => {
        // Set locale after messages are fully loaded
        i18n.global.locale.value = locale;
        console.log(`[i18n DEBUG] Set vue-i18n locale.value to: ${locale} AFTER loading messages`);
        
        // Dispatch event after locale change with loaded messages
        window.dispatchEvent(new CustomEvent('localeChanged', { 
          detail: { locale, loaded: true } 
        }));
        
        console.log(`Locale messages for ${locale} loaded successfully and locale changed`);
      })
      .catch(err => {
        console.error(`Error loading messages for ${locale}:`, err);
        
        // Nếu locale chính thất bại và không phải tiếng Anh, chuyển sang tiếng Anh
        if (locale !== DEFAULT_LOCALE) {
          console.warn(`Falling back to ${DEFAULT_LOCALE} due to load failure`);
          
          // Ensure English is loaded
          if (!i18n.global.messages.value[DEFAULT_LOCALE] || 
              Object.keys(i18n.global.messages.value[DEFAULT_LOCALE]).length <= 3) {
            loadLocaleMessages(DEFAULT_LOCALE).then(() => {
              i18n.global.locale.value = DEFAULT_LOCALE;
            }).catch(() => {
              // Even if English load fails, we have basic messages
              console.warn('Even English load failed, using basic messages');
              i18n.global.locale.value = DEFAULT_LOCALE;
            });
          } else {
            i18n.global.locale.value = DEFAULT_LOCALE;
          }
        }
      });
  } else {
    console.log(`Using already loaded messages for ${locale}`);
    // Only change locale after confirming messages are loaded
    i18n.global.locale.value = locale;
    console.log(`[i18n DEBUG] Set vue-i18n locale.value to: ${locale} with pre-loaded messages`);
    
    // Always dispatch event for immediate UI update
    window.dispatchEvent(new CustomEvent('localeChanged', { 
      detail: { locale, immediate: true } 
    }));
  }
}

// Initialize locale on app start
export function initializeI18n() {
  const currentLocale = i18n.global.locale.value as LocaleType;
  
  try {
    document.querySelector('html')?.setAttribute('lang', currentLocale);
  } catch (e) {
    console.error('Error setting HTML lang attribute:', e);
  }
  
  console.log('i18n initialized with locale:', currentLocale);
  
  // Danh sách ưu tiên: locale hiện tại, default locale, locale khác
  const priorityList: LocaleType[] = [
    currentLocale,
    ...(currentLocale !== DEFAULT_LOCALE ? [DEFAULT_LOCALE] : []),
    ...Object.values(LOCALES).filter(l => l !== currentLocale && l !== DEFAULT_LOCALE)
  ];
  
  // Trước tiên, đảm bảo rằng chúng ta có ít nhất những message cơ bản
  Object.entries(basicMessages).forEach(([locale, messages]) => {
    if (!i18n.global.messages.value[locale] || Object.keys(i18n.global.messages.value[locale]).length === 0) {
      i18n.global.setLocaleMessage(locale as LocaleType, messages);
    }
  });
  
  // Load các locale theo thứ tự ưu tiên
  const loadAllLocales = async () => {
    try {
      // Load locale hiện tại trước
      await loadLocaleMessages(priorityList[0]);
      console.log(`Current locale ${priorityList[0]} loaded`);
      
      // Kiểm tra messages ngay lập tức
      const messageCount = Object.keys(i18n.global.messages.value[priorityList[0] as LocaleType]).length;
      console.log(`Current locale has ${messageCount} messages`);
      
      // Nạp các locale còn lại theo thứ tự ưu tiên
      for (let i = 1; i < priorityList.length; i++) {
        try {
          await loadLocaleMessages(priorityList[i]);
        } catch (e) {
          console.warn(`Non-critical error loading ${priorityList[i]}:`, e);
        }
      }
      
      // Thông báo là đã load xong
      console.log('All locales loaded according to priority');
      window.dispatchEvent(new CustomEvent('i18nInitialized', { 
        detail: { locale: currentLocale, complete: true } 
      }));
    } catch (error) {
      console.error(`Error during locale initialization:`, error);
      // Vẫn dispatch event để app hoạt động dù có lỗi
      window.dispatchEvent(new CustomEvent('i18nInitialized', { 
        detail: { locale: currentLocale, error: true } 
      }));
    }
  };
  
  // Start loading locales
  loadAllLocales();
}

export default i18n 

/**
 * Wrapper around i18n.t that adds additional functionality
 */
export function t(key: string, options?: TranslateOptions): string {
  try {
    // Đầu tiên thử dùng i18n.global.t trực tiếp
    const translatedValue = i18n.global.t(key, options as Record<string, unknown>) as string;
    
    // Nếu kết quả là key gốc, có thể là translation bị thiếu
    if (translatedValue === key) {
      // Thử truy cập trực tiếp qua getLocaleMessage để xem key có tồn tại không
      const locale = i18n.global.locale.value as LocaleType;
      const messages = i18n.global.getLocaleMessage(locale);
      
      // Tách key thành các phần
      const parts = key.split('.');
      let currentValue: any = messages;
      
      // Duyệt qua từng phần để tìm giá trị
      for (const part of parts) {
        if (!currentValue || typeof currentValue !== 'object') {
          return key; // Không tìm thấy
        }
        currentValue = currentValue[part];
      }
      
      // Nếu tìm thấy giá trị, sử dụng nó
      if (currentValue && typeof currentValue === 'string') {
        // Xử lý tham số nếu có
        if (options && typeof options === 'object') {
          return Object.entries(options).reduce((message, [paramKey, paramValue]) => {
            return message.replace(new RegExp(`{${paramKey}}`, 'g'), String(paramValue));
          }, currentValue);
        }
        return currentValue;
      }
    }
    
    return translatedValue;
  } catch (e) {
    console.error(`[i18n] Error translating key "${key}":`, e);
    return key;
  }
}

export { getBrowserLocale }; 